import Parameter from "../../../../common/components/Parameter";
import { type ProductData } from "~/domains/product/types/ProductData";
import { type LineItem, PackageEntrySource } from "~/features/cart/cart/models/CartData";
import { Link } from "../../../../common/components/Link";
import { getContractDetailsUrl, getQuoteDetailsUrl } from "../../../../utils/url/urlUtils";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";
import { useTranslation } from "react-i18next";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

type BaseParamProps = JSX.IntrinsicElements["div"] & {
  product: ProductData;
  lineItem: LineItem;
  "data-test-id"?: string;
  children?: never;
};

export const QuoteItemParam = ({ product, lineItem, ...rest }: BaseParamProps) => {
  const { t } = useTranslation();

  if (lineItem.source !== PackageEntrySource.QUOTE) {
    return null;
  }

  const { quoteLine, quoteNumber } = lineItem;
  const url = getQuoteDetailsUrl(quoteNumber);

  return (
    <Parameter label={t("lineItem.param.quote")} {...rest}>
      <Link href={url}>{quoteNumber}</Link>, {quoteLine}
    </Parameter>
  );
};

export const ContractItemParam = ({ product, lineItem, ...rest }: BaseParamProps) => {
  const { t } = useTranslation();

  if (lineItem.source !== PackageEntrySource.CONTRACT) {
    return null;
  }

  const { sapMaterialNumber } = product;
  const { packCode, contractNumber, contractLine } = lineItem;

  const url = getContractDetailsUrl({ sapMaterialNumber, packCode, contractPosition: contractLine, contractNumber });

  return (
    <Parameter label={t("lineItem.param.contract")} {...rest}>
      <Link href={url} target="_blank" iconAfter={<OpenInNewIcon fontSize="small" />}>
        {contractNumber}
      </Link>
    </Parameter>
  );
};

interface PriceReferenceParamProps extends Omit<BaseParamProps, "lineItem"> {
  lineItem: Pick<LineItem, "priceReference">;
}
export const PriceReferenceParam = ({ product, lineItem, ...rest }: PriceReferenceParamProps) => {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();

  if (!isPriceReferenceParamVisible(selection)) {
    return null;
  }

  // label key cannot be undefined because we checked isPriceReferenceParamVisible() before
  const label = t(getPriceReferenceLabelKey(selection)!);

  return (
    <Parameter label={label} cutOverflow={true} {...rest}>
      {getPriceReferenceLabel(lineItem.priceReference, selection)}
    </Parameter>
  );
};

export const getPriceReferenceLabel = (key: string, selection?: PriceReferenceSelection) => {
  if (!key) {
    return "-";
  }
  const option =
    selection?.options.find(({ code }) => code === key) ??
    (selection?.initialValue.code === key ? selection.initialValue : undefined);
  return option?.description?.trim() ? `${option.code} - ${option.description}` : option?.code ?? key;
};

export const isPriceReferenceParamVisible = (selection?: PriceReferenceSelection) => {
  return hasFixedPriceReference(selection) || userCanSeePriceReference(selection);
};

export const userCanSeePriceReference = (selection?: PriceReferenceSelection) => Boolean(selection?.options.length);
export const hasFixedPriceReference = (selection?: PriceReferenceSelection) =>
  Boolean(selection?.initialValue.code && selection.options.length === 0);

export const getPriceReferenceLabelKey = (selection?: PriceReferenceSelection) => {
  if (hasFixedPriceReference(selection)) {
    return "productDetailPage.endCustomerLabel";
  } else if (userCanSeePriceReference(selection)) {
    return "component.dropdown.label.priceReference";
  }
};
